import React from "react";
import { useNavigate } from "react-router-dom";

import { parseDuration } from "../utils/duration";
import { parseDateTime } from "../utils/date";

import {
    AiFillVideoCamera,
    AiFillClockCircle,
    AiOutlineMore,
    AiFillDelete,
    AiOutlineEdit,
} from "react-icons/ai";
import { MdVideoLibrary } from "react-icons/md";

import blank from "../../assets/blank.jpg";

export interface collection {
    id: number;
    name: string;
    user_id: number;
    created_at: string;
    num_videos: number;
    total_duration: number;
    latest_thumbnail: string;
    latest_video_id: string;
    expired?: boolean;
}

const parseVideoNumber = (num_videos: number) => {
    return num_videos === 1 ? `${num_videos} video` : `${num_videos} videos`;
};

interface IndexTileProps {
    collection: collection;
    onRename: (collection: collection) => void;
    onDelete: (collection: collection) => void;
    isReviewTile?: boolean;
    expired?: boolean;
}

const IndexTile: React.FC<IndexTileProps> = ({ collection, onRename, onDelete, isReviewTile=false }) => {
    const navigate = useNavigate();

    const handleClick = () => {
        if (collection.expired) {
            return;
        }
        if (isReviewTile) {
            navigate(`/quick-review/collections/${collection.id}`);
            return;
        }
        navigate(`/collections/${collection.id}`);
    };

    const renderThumbnail = () => {
        if (collection.num_videos === 0) {
            return (
                <div className={`flex justify-center items-center h-56 w-full bg-gray-300 ${collection.expired ? "cursor-not-allowed" : "cursor-pointer"}`}>
                    <MdVideoLibrary className="text-gray-500" size={48} />
                </div>
            );
        }

        // const thumbnail = `https://zwive.s3.us-west-1.amazonaws.com/${collection.user_id}/thumbnails/${collection.latest_video_id}/8.jpg`;

        return (
            <img
                src={collection.latest_thumbnail}
                alt="thumbnail"
                onError={e => {
                    const target = e.target as HTMLImageElement;
                    target.onerror = null;
                    target.src = blank;
                }}
                onClick={handleClick}
                className={`h-56 object-cover w-full ${collection.expired ? "cursor-not-allowed" : "cursor-pointer"}`}
            />
        );
    };

    return (
        <div
            className={`card card-compact w-full bg-base-100 outline outline-gray-200 hover:shadow-md relative rounded`}
        >
            {collection.expired && (
                <div className="absolute top-2 right-2 bg-red-500 text-white text-xs font-semibold px-2 py-1 rounded z-10">
                    EXPIRED
                </div>
            )}
            <div className={`${collection.expired ? "opacity-50 cursor-not-allowed" : ""}`}>
                <figure onClick={handleClick} className={`w-full ${collection.expired ? "cursor-not-allowed" : "cursor-pointer"}`}>
                    {renderThumbnail()}
                </figure>
                <div className="card-body">
                    <h2 className={`card-title ${collection.expired ? "" : "hover:underline cursor-pointer"}`} onClick={handleClick}>
                        {collection.name}
                    </h2>
                    <div className="flex items-center gap-3">
                        <AiFillVideoCamera className="inline" size={16} color="gray" />
                        <p>
                            {parseVideoNumber(collection.num_videos)} (
                            {parseDuration(collection.total_duration)})
                        </p>
                    </div>
                    <div className="flex items-center gap-3">
                        <AiFillClockCircle className="inline" size={16} color="gray" />
                        <p>Created on {parseDateTime(collection.created_at)}</p>
                    </div>
                    <div className="card-actions justify-between items-center">
                        <div className="dropdown dropdown-start">
                            <AiOutlineMore
                                tabIndex={0}
                                className={`cursor-pointer hover:bg-slate-100 rounded-full p-1 ${collection.expired ? "pointer-events-none" : ""}`}
                                size={26}
                                color="black"
                            />
                            {collection.user_id === Number(localStorage.getItem("user_id")) && !collection.expired && (
                                <ul
                                    tabIndex={0}
                                    className="dropdown-content menu p-2 shadow outline outline-gray-200 bg-base-100 rounded-box w-52 z-10"
                                >
                                    <li>
                                        <button onClick={() => onRename(collection)}>
                                            <AiOutlineEdit className="inline" size={16} />
                                            Rename Collection
                                        </button>
                                    </li>
                                    <li>
                                        <button onClick={() => onDelete(collection)}>
                                            <AiFillDelete className="inline" size={16} />
                                            Delete
                                        </button>
                                    </li>
                                </ul>
                            )}
                        </div>
                        <button
                            className={`btn btn-primary text-white ${collection.expired ? "btn-disabled" : ""}`}
                            onClick={handleClick}
                        >
                            Explore
                        </button>
                    </div>
                </div>
            </div>
        </div>
    );
};

export default IndexTile;
